import axios from 'axios';
import { Language } from 'common/interfaces/api';
import { cn, timeSince } from 'common/utils';
import { ScreenName } from 'common/utils/pp_tracking';
import { Button } from 'components/v2/atoms/button';
import { VerifyCreatorIcon } from 'components/v2/atoms/icon';
import {
  FreePostIcon,
  PostTypeIconWithText,
} from 'components/v2/atoms/post-type-icon';
import dayjs from 'lib/dayjs';
import Link from 'next/link';
import { useContext, useEffect, useState } from 'react';
import { PostTypeEnum } from 'service/timeline';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';
import { useMounted } from 'common/utils/hooks/useDidMount';

import { PostItemContext } from './post-item-context';

const UserInfo = () => {
  const { post, screen } = useContext(PostItemContext);
  const { user_info, type, created, free_mins, is_aff, is_mine, user_topic } =
    post;
  const mounted = useMounted();
  const [isFollowing, setIsFollowing] = useState(!!user_info?.is_follow);
  useEffect(() => {
    setIsFollowing(!!user_info?.is_follow);
  }, [user_info?.is_follow]);

  const currentUser = useStore((state) => state.currentUser);
  const refetchCurrentProfile = useStore(
    (state) => state.refetchCurrentProfile,
  );
  const refetchPosts = useStore((state) => state.refetchPosts);
  const showLogin = useGlobalStore((state) => state.showLogin);
  if (!user_info) return null;

  const handleFollowClick = async (userId: number, isFollowed: boolean) => {
    if (currentUser) {
      const action = isFollowed ? 'unfollow' : 'follow';
      setIsFollowing(!isFollowed);
      try {
        await axios.post(`/users/follows`, {
          user_id: userId,
          action: action,
        });
        refetchCurrentProfile();
        refetchPosts();
      } catch (error) {
        setIsFollowing(isFollowed);
      }
    } else {
      showLogin({
        showingTitle: 'login',
        redirectUrl: `/${user_info.username}`,
      });
    }
  };

  const isScheduledPost = new Date(created) > new Date();
  const fallbackTimeText = dayjs.utc(created).format('YYYY/MM/DD HH:mm');
  const scheduledTimeText = dayjs(created).format('YYYY/MM/DD HH:mm');
  const timeSinceText = mounted
    ? isScheduledPost
      ? `${scheduledTimeText}に公開予定`
      : timeSince(new Date(created), Language.Ja)
    : fallbackTimeText;

  const getPostTypeIcon = () => {
    if (type === PostTypeEnum.Gold)
      return (
        <PostTypeIconWithText
          text="G"
          className="size-4 border-gray-700 text-[8px]"
        />
      );
    if (type === PostTypeEnum.Free)
      return (
        <FreePostIcon
          className="size-4 border border-gray-700"
          iconClassName="size-2.5"
        />
      );
    if (type === PostTypeEnum.Plus)
      return (
        <PostTypeIconWithText
          text="P+"
          className="size-4 border-gray-700 text-[8px]"
        />
      );
    if (type === PostTypeEnum.Paid && free_mins === 0)
      return (
        <PostTypeIconWithText
          text="P"
          className="size-4 border-gray-700 text-[8px]"
        />
      );
    if (type === PostTypeEnum.Paid && free_mins > 0)
      return (
        <PostTypeIconWithText
          text={`P${free_mins}`}
          className={cn('size-4 border-gray-700 text-[8px]', {
            'text-[6px]': free_mins >= 10,
          })}
        />
      );
    return null;
  };

  const getSelectedLabel = () => {
    if (type === PostTypeEnum.Free) return '無料';
    if (type === PostTypeEnum.Plus) return 'プライムPlus';
    if (type === PostTypeEnum.Paid && free_mins === 0) return 'プライム';
    if (type === PostTypeEnum.Paid && free_mins > 0)
      return `${free_mins}分無料`;
    return '';
  };

  return (
    <div className="mb-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="relative">
          <Link
            href={`/${user_info.username}`}
            className={cn(
              'size-10 shrink-0 overflow-hidden rounded-full border-[0.5px] border-solid border-line-default',
              {
                'border-[2px] border-[#ebebeb] p-[1.2px]':
                  user_info.has_new ||
                  Boolean(user_info.has_tip) ||
                  user_info.live,
                'border-[#4ae5c9]': user_info.has_new,
                'border-[#09C1D4]': user_info.has_tip === 1,
                'border-[#B381FF]': user_info.has_tip === 2,
                'border-[#F9B48D]': user_info.has_tip === 3,
                'border-[#c3c3c3]': user_info.has_tip === 4,
                'border-[#ddba04]': user_info.has_tip === 5,
                'border-pp-error-primary': user_info.live,
              },
            )}
          >
            <img
              src={user_info.avatar || '/images/icons/default-avatar1.png'}
              alt={user_info.name}
              className="shrink-0 cursor-pointer rounded-full object-cover hover:scale-105"
            />
          </Link>
          {user_info.live && (
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="flex h-5 w-10 items-center justify-center rounded border border-white bg-pp-error-primary text-[8px] font-semibold text-white">
                LIVE
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <Link href={`/${user_info.username}`} className="flex">
              <span className="line-clamp-1 text-xs font-semibold text-pp-text-primary hover:underline md:text-sm">
                {user_info.name}
              </span>
            </Link>

            {/* Verified badge */}
            {user_info.is_cre && !is_aff && (
              <VerifyCreatorIcon className="size-3.5 shrink-0 text-pp-green-primary" />
            )}

            {/* Follow button */}
            {!is_mine && !isFollowing && !is_aff && (
              <>
                <span className="mx-0.5 text-[12px] leading-[16px] text-pp-text-tertiary md:mx-1 md:text-[13px]">
                  •
                </span>
                <Button
                  onClick={() => handleFollowClick(user_info.id, isFollowing)}
                  variant="outlineGreen"
                  size="sm"
                  className="h-auto p-0 text-xs font-semibold text-pp-green-primary hover:scale-105 hover:bg-white hover:text-green-700"
                >
                  フォロー
                </Button>
              </>
            )}
            {user_topic &&
              user_topic?.name &&
              !is_aff &&
              screen !== ScreenName.TopicPosts && (
                <>
                  <span className="mx-0.5 text-[12px] leading-[16px] text-pp-text-tertiary md:mx-1 md:text-[13px]">
                    •
                  </span>
                  <span className="text-[12px] leading-[16px] text-pp-text-tertiary md:text-[13px]">{`>`}</span>
                  <Link
                    href={`/p/post-topic/${encodeURIComponent(user_topic.name)}`}
                    className="line-clamp-1 text-[12px] leading-[16px] text-pp-text-secondary hover:underline md:text-[13px]"
                  >
                    {user_topic.name}
                  </Link>
                </>
              )}
          </div>

          {/* Time và post type indicators */}
          <div className="flex items-center gap-2 text-xs text-pp-text-secondary">
            <span>{is_aff ? 'Sponsored' : timeSinceText}</span>
            {!is_aff && type !== PostTypeEnum.Free && (
              <div className="flex items-center gap-1">
                {getPostTypeIcon()}
                <span>{getSelectedLabel()}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserInfo;
